import React, { useState, useEffect, useCallback } from 'react';
import {
  Shield, ShieldCheck, ShieldAlert, ShieldOff,
  Plus, RotateCcw, Trash2, RefreshCw,
  Eye, EyeOff, ChevronDown, ChevronRight,
  Clock, CheckCircle, XCircle, AlertTriangle, Info,
  Key, Server, Copy, Download,
} from 'lucide-react';
import { mojaloopApi } from '../services/api';
import { formatDate, formatRelativeTime } from '../utils/helpers';
import { useAuth } from '../contexts/AuthContext';
import { canManageSigningMaterial } from '../utils/roles';

// ─── Status helpers ───────────────────────────────────────────────────────────

// The engine serialises the entity's isHubKey() getter as "hubKey"; the import
// request still sends "isHubKey". Read either, so a registered Hub key is listed
// as the Hub key.
const isHub = (key) => Boolean(key?.hubKey ?? key?.isHubKey);

function expiryMeta(key) {
  if (!key.expiresAt) return { label: 'No expiry', cls: 'text-gray-500', icon: null };
  const days = key.daysUntilExpiry ?? 9999;
  if (days < 0)  return { label: 'Expired',               cls: 'text-danger-700',  icon: XCircle };
  if (days <= 30) return { label: `Expires in ${days}d`,  cls: 'text-warning-700', icon: AlertTriangle };
  if (days <= 90) return { label: `Expires in ${days}d`,  cls: 'text-orange-600',  icon: Clock };
  return           { label: `Expires in ${days}d`,        cls: 'text-success-700', icon: CheckCircle };
}

function StatusBadge({ active, isHubKey }) {
  if (!active) return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
      <ShieldOff className="w-3 h-3" /> Inactive
    </span>
  );
  if (isHubKey) return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
      <Server className="w-3 h-3" /> Hub Key
    </span>
  );
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-success-100 text-success-700">
      <ShieldCheck className="w-3 h-3" /> Active
    </span>
  );
}

// ─── PEM Preview — parse client-side before submitting ───────────────────────

function parsePemPreview(pem) {
  if (!pem || pem.trim().length < 20) return null;
  const isCert = pem.includes('BEGIN CERTIFICATE');
  const isKey  = pem.includes('BEGIN PUBLIC KEY');
  if (!isCert && !isKey) return null;
  const lineCount = pem.trim().split('\n').length;
  return {
    type:    isCert ? 'X.509 Certificate' : 'Public Key (SubjectPublicKeyInfo)',
    hasExpiry: isCert,
    lines:   lineCount,
  };
}

// ─── Import / Rotate modal ────────────────────────────────────────────────────

function KeyImportModal({ mode, existingKey, presetHubKey = false, onClose, onSuccess }) {
  const isRotate = mode === 'rotate';
  const [form, setForm] = useState({
    fspId:         isRotate ? existingKey.fspId : '',
    publicKeyPem:  '',
    isHubKey:      isRotate ? isHub(existingKey) : presetHubKey,
    description:   isRotate ? (existingKey.description ?? '') : '',
    rotationNotes: '',
  });
  const [pemPreview, setPemPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError]           = useState('');
  const [showPem, setShowPem]       = useState(false);

  const handlePemChange = (val) => {
    setForm(f => ({ ...f, publicKeyPem: val }));
    setPemPreview(parsePemPreview(val));
  };

  const handleSubmit = async () => {
    if (!form.publicKeyPem.trim()) { setError('PEM is required.'); return; }
    if (!isRotate && !form.fspId.trim()) { setError('FSP ID is required.'); return; }
    setError('');
    setSubmitting(true);
    try {
      let result;
      if (isRotate) {
        result = await mojaloopApi.rotateFspKey(existingKey.id, {
          publicKeyPem:  form.publicKeyPem,
          rotationNotes: form.rotationNotes,
        });
      } else {
        result = await mojaloopApi.importFspKey({
          fspId:         form.fspId,
          publicKeyPem:  form.publicKeyPem,
          isHubKey:      form.isHubKey,
          description:   form.description,
          rotationNotes: form.rotationNotes,
        });
      }
      onSuccess(result);
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Import failed.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${isRotate ? 'bg-warning-100' : 'bg-blue-100'}`}>
              {isRotate
                ? <RotateCcw className="w-5 h-5 text-warning-600" />
                : <Plus       className="w-5 h-5 text-blue-600" />}
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                {isRotate ? `Rotate Key — ${existingKey.fspId}` : 'Import FSP Key'}
              </h2>
              <p className="text-sm text-gray-500">
                {isRotate
                  ? 'The current key will be deactivated and chained to the new one.'
                  : 'Paste the PEM-encoded public key or X.509 certificate.'}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">×</button>
        </div>

        <div className="px-6 py-5 space-y-5">

          {/* FSP ID (import only) */}
          {!isRotate && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">FSP ID <span className="text-danger-500">*</span></label>
                <input
                  className="input w-full"
                  placeholder="e.g. AcmeFSP, switch"
                  value={form.fspId}
                  onChange={e => setForm(f => ({ ...f, fspId: e.target.value }))}
                />
                <p className="text-xs text-gray-500 mt-1">Must match the FSP ID as registered in the Mojaloop Hub.</p>
              </div>
              <div className="flex flex-col justify-end pb-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    className="w-4 h-4 rounded border-gray-300 text-blue-600"
                    checked={form.isHubKey}
                    onChange={e => setForm(f => ({ ...f, isHubKey: e.target.checked }))}
                  />
                  <span className="text-sm font-medium text-gray-700">This is the Hub / switch key</span>
                </label>
                <p className="text-xs text-gray-500 mt-1 ml-6">Check this for the key provided by the switch operator, not a participant DFSP.</p>
              </div>
            </div>
          )}

          {/* PEM input */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="block text-sm font-medium text-gray-700">
                Public Key PEM <span className="text-danger-500">*</span>
              </label>
              <button
                onClick={() => setShowPem(p => !p)}
                className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1"
              >
                {showPem ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                {showPem ? 'Hide' : 'Show'}
              </button>
            </div>
            <textarea
              rows={showPem ? 10 : 4}
              className="input w-full font-mono text-xs resize-y"
              placeholder={'-----BEGIN CERTIFICATE-----\nMIIB...\n-----END CERTIFICATE-----\n\nor:\n\n-----BEGIN PUBLIC KEY-----\nMIIB...\n-----END PUBLIC KEY-----'}
              value={form.publicKeyPem}
              onChange={e => handlePemChange(e.target.value)}
            />

            {/* Live PEM preview */}
            {pemPreview && (
              <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-start gap-2">
                <Info className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                <div className="text-xs text-blue-800 space-y-0.5">
                  <p><span className="font-medium">Detected:</span> {pemPreview.type}</p>
                  {pemPreview.hasExpiry && (
                    <p className="text-blue-600">Certificate expiry will be extracted and tracked automatically.</p>
                  )}
                  {!pemPreview.hasExpiry && (
                    <p className="text-blue-600">Bare public key — no expiry tracking (no certificate).</p>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Description (import only) */}
          {!isRotate && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <input
                className="input w-full"
                placeholder="e.g. Hub JWS verification key, received on 2026-04-01"
                value={form.description}
                onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              />
            </div>
          )}

          {/* Rotation notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {isRotate ? 'Rotation Notes' : 'Import Notes'}
            </label>
            <textarea
              rows={2}
              className="input w-full text-sm"
              placeholder={isRotate
                ? 'e.g. Scheduled quarterly rotation, new key received by secure email on 2026-05-01'
                : 'e.g. Initial key provided by Hub operator during onboarding'}
              value={form.rotationNotes}
              onChange={e => setForm(f => ({ ...f, rotationNotes: e.target.value }))}
            />
          </div>

          {/* Warning for rotate */}
          {isRotate && (
            <div className="p-3 bg-warning-50 border border-warning-200 rounded-lg flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-warning-600 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-warning-800">
                The current key (fingerprint <code className="font-mono text-xs">{existingKey.fingerprint?.substring(0, 17)}…</code>) will be
                deactivated immediately. Ensure the Hub is already using the new key before proceeding.
              </p>
            </div>
          )}

          {error && (
            <div className="p-3 bg-danger-50 border border-danger-200 rounded-lg text-sm text-danger-700">
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl">
          <button onClick={onClose} className="btn-secondary">Cancel</button>
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className={`btn-primary flex items-center gap-2 ${isRotate ? 'bg-warning-600 hover:bg-warning-700 border-warning-600' : ''}`}
          >
            {submitting && <RefreshCw className="w-4 h-4 animate-spin" />}
            {isRotate ? 'Rotate Key' : 'Import Key'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Key Card ─────────────────────────────────────────────────────────────────

function KeyCard({ keyData, onRotate, onDeactivate, onRefresh, canManage = false }) {
  const [expanded, setExpanded]       = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [copied, setCopied]           = useState(false);
  const expiry = expiryMeta(keyData);
  const ExpiryIcon = expiry.icon;

  const copyFingerprint = async () => {
    await navigator.clipboard.writeText(keyData.fingerprint ?? '');
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className={`card border-l-4 ${
      !keyData.active           ? 'border-l-gray-300 opacity-60' :
      isHub(keyData)            ? 'border-l-blue-500' :
      (keyData.daysUntilExpiry != null && keyData.daysUntilExpiry < 30) ? 'border-l-warning-500' :
      'border-l-success-500'
    }`}>
      <div className="card-header">
        {/* Top row: FSP ID + badges */}
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className={`p-2 rounded-lg flex-shrink-0 ${isHub(keyData) ? 'bg-blue-100' : 'bg-gray-100'}`}>
              {isHub(keyData)
                ? <Server className="w-5 h-5 text-blue-600" />
                : <Key    className="w-5 h-5 text-gray-600" />}
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-semibold text-gray-900 text-sm">{keyData.fspId}</span>
                <StatusBadge active={keyData.active} isHubKey={isHub(keyData)} />
                {keyData.keyType && (
                  <span className="px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded text-xs font-mono">
                    {keyData.keyType}
                  </span>
                )}
              </div>
              {keyData.description && (
                <p className="text-xs text-gray-500 mt-0.5 truncate">{keyData.description}</p>
              )}
            </div>
          </div>

          {/* Actions */}
          {keyData.active && canManage && (
            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                onClick={() => onRotate(keyData)}
                className="btn-secondary text-xs flex items-center gap-1 py-1.5 px-3"
                title="Rotate key"
              >
                <RotateCcw className="w-3.5 h-3.5" />
                Rotate
              </button>
              <button
                onClick={() => onDeactivate(keyData)}
                className="p-1.5 text-gray-400 hover:text-danger-600 hover:bg-danger-50 rounded-lg transition-colors"
                title="Deactivate"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>

        {/* Metadata row */}
        <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1.5 text-xs text-gray-500">

          {/* Fingerprint */}
          {keyData.fingerprint && (
            <div className="flex items-center gap-1.5">
              <Shield className="w-3.5 h-3.5 text-gray-400" />
              <span className="font-mono text-gray-600">
                {keyData.fingerprint.substring(0, 23)}…
              </span>
              <button
                onClick={copyFingerprint}
                className="text-gray-400 hover:text-gray-600 transition-colors"
                title="Copy full fingerprint"
              >
                {copied ? <CheckCircle className="w-3 h-3 text-success-600" /> : <Copy className="w-3 h-3" />}
              </button>
            </div>
          )}

          {/* Key size */}
          {keyData.keySize && (
            <span>RSA {keyData.keySize}-bit</span>
          )}

          {/* Expiry */}
          <div className={`flex items-center gap-1 ${expiry.cls}`}>
            {ExpiryIcon && <ExpiryIcon className="w-3.5 h-3.5" />}
            {expiry.label}
            {keyData.expiresAt && (
              <span className="text-gray-400">({formatDate(keyData.expiresAt, 'dd MMM yyyy')})</span>
            )}
          </div>

          {/* Verified count */}
          <span className="flex items-center gap-1">
            <CheckCircle className="w-3.5 h-3.5 text-gray-400" />
            {(keyData.verifiedCount ?? 0).toLocaleString()} verifications
          </span>

          {/* Last used */}
          {keyData.lastUsedAt && (
            <span className="flex items-center gap-1">
              <Clock className="w-3.5 h-3.5 text-gray-400" />
              Last used {formatRelativeTime(keyData.lastUsedAt)}
            </span>
          )}

          {/* Imported by */}
          {keyData.importedBy && (
            <span>Imported by <span className="text-gray-700 font-medium">{keyData.importedBy}</span> {formatRelativeTime(keyData.createdAt)}</span>
          )}
        </div>
      </div>

      {/* Expandable details */}
      <div className="border-t border-gray-100">
        <button
          onClick={() => setExpanded(e => !e)}
          className="w-full flex items-center justify-between px-5 py-2.5 text-xs text-gray-500 hover:bg-gray-50 transition-colors"
        >
          <span>Details</span>
          {expanded ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
        </button>

        {expanded && (
          <div className="px-5 pb-4 space-y-3">
            {/* Full fingerprint */}
            {keyData.fingerprint && (
              <div>
                <p className="text-xs font-medium text-gray-500 mb-1">Full SHA-256 Fingerprint</p>
                <div className="bg-gray-50 rounded-lg p-2.5 font-mono text-xs text-gray-700 break-all flex items-start justify-between gap-2">
                  <span>{keyData.fingerprint}</span>
                  <button onClick={copyFingerprint} className="text-gray-400 hover:text-gray-600 flex-shrink-0">
                    {copied ? <CheckCircle className="w-3.5 h-3.5 text-success-600" /> : <Copy className="w-3.5 h-3.5" />}
                  </button>
                </div>
              </div>
            )}

            {/* Rotation notes */}
            {keyData.rotationNotes && (
              <div>
                <p className="text-xs font-medium text-gray-500 mb-1">Notes</p>
                <p className="text-xs text-gray-700 bg-gray-50 rounded-lg p-2.5">{keyData.rotationNotes}</p>
              </div>
            )}

            {/* Rotation history chain */}
            {keyData.previousKeyId && (
              <div>
                <button
                  onClick={() => setShowHistory(h => !h)}
                  className="text-xs text-primary-600 hover:text-primary-700 flex items-center gap-1"
                >
                  {showHistory ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                  Replaced key id={keyData.previousKeyId}
                </button>
              </div>
            )}

            {/* PEM download */}
            <div>
              <button
                onClick={() => {
                  const blob = new Blob([keyData.publicKeyPem], { type: 'text/plain' });
                  const url  = URL.createObjectURL(blob);
                  const a    = document.createElement('a');
                  a.href     = url;
                  a.download = `${keyData.fspId}-public-key.pem`;
                  a.click();
                  URL.revokeObjectURL(url);
                }}
                className="text-xs text-primary-600 hover:text-primary-700 flex items-center gap-1"
              >
                <Download className="w-3.5 h-3.5" />
                Download PEM
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

const FspKeys = () => {
  // Importing, rotating and deactivating keys is for admins; the engine refuses it
  // to anyone else, so the controls are not offered.
  const { user } = useAuth();
  const canManage = canManageSigningMaterial(user);
  const [keys, setKeys]               = useState([]);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState(null);
  const [modal, setModal]             = useState(null); // null | { mode: 'import' | 'rotate', key? }
  const [confirmDeactivate, setConfirmDeactivate] = useState(null);
  const [toast, setToast]             = useState(null);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await mojaloopApi.getFspKeys({ activeOnly: false });
      setKeys(Array.isArray(data) ? data : []);
    } catch (err) {
      setError('Failed to load FSP keys. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleImportSuccess = (newKey) => {
    setModal(null);
    showToast(`Key for ${newKey.fspId} imported successfully.`);
    load();
  };

  const handleRotateSuccess = (newKey) => {
    setModal(null);
    showToast(`Key for ${newKey.fspId} rotated successfully.`);
    load();
  };

  const handleDeactivate = async (key) => {
    try {
      await mojaloopApi.deactivateFspKey(key.id);
      setConfirmDeactivate(null);
      showToast(`Key for ${key.fspId} deactivated.`, 'warning');
      load();
    } catch {
      showToast('Deactivation failed. Please try again.', 'error');
    }
  };

  // Split into Hub key(s) and participant keys
  const activeKeys   = keys.filter(k => k.active);
  const inactiveKeys = keys.filter(k => !k.active);
  const hubKeys      = activeKeys.filter(isHub);
  const participantKeys = activeKeys.filter(k => !isHub(k));

  // Expiry warnings
  const expiringSoon = activeKeys.filter(k => k.daysUntilExpiry != null && k.daysUntilExpiry <= 30);

  return (
    <div className="space-y-6">

      {/* Toast */}
      {toast && (
        <div className={`fixed top-5 right-5 z-50 px-4 py-3 rounded-xl shadow-lg text-sm font-medium flex items-center gap-2 ${
          toast.type === 'error'   ? 'bg-danger-600 text-white' :
          toast.type === 'warning' ? 'bg-warning-500 text-white' :
          'bg-success-600 text-white'
        }`}>
          {toast.type === 'success' && <CheckCircle className="w-4 h-4" />}
          {toast.type === 'warning' && <AlertTriangle className="w-4 h-4" />}
          {toast.type === 'error'   && <XCircle className="w-4 h-4" />}
          {toast.message}
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">FSP Keys</h1>
          <p className="text-gray-600 mt-1">
            Manage JWS public keys for FSPIOP callback verification — Hub key and participant FSP keys.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={load}
            disabled={loading}
            className="btn-secondary flex items-center gap-2"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          {canManage && (
          <button
            onClick={() => setModal({ mode: 'import' })}
            className="btn-primary flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Import Key
          </button>
          )}
        </div>
      </div>

      {/* Expiry warnings banner */}
      {expiringSoon.length > 0 && (
        <div className="p-4 bg-warning-50 border border-warning-200 rounded-xl flex items-start gap-3">
          <ShieldAlert className="w-5 h-5 text-warning-600 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm font-medium text-warning-800">
              {expiringSoon.length} key{expiringSoon.length > 1 ? 's' : ''} expiring within 30 days
            </p>
            <p className="text-sm text-warning-700 mt-0.5">
              {expiringSoon.map(k => k.fspId).join(', ')} — rotate before expiry to avoid JWS verification failures.
            </p>
          </div>
        </div>
      )}

      {/* Stats row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Active Keys',    value: activeKeys.length,          color: 'text-success-600' },
          { label: 'Hub Key',        value: hubKeys.length ? '✓' : '✗', color: hubKeys.length ? 'text-blue-600' : 'text-danger-600' },
          { label: 'Participant FSPs', value: participantKeys.length,   color: 'text-gray-900' },
          { label: 'Expiring Soon',  value: expiringSoon.length,        color: expiringSoon.length ? 'text-warning-600' : 'text-gray-400' },
        ].map(s => (
          <div key={s.label} className="stat-card text-center">
            <p className="text-xs text-gray-500 mb-1">{s.label}</p>
            <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
          </div>
        ))}
      </div>

      {/* Loading / Error */}
      {loading && !keys.length && (
        <div className="flex items-center justify-center py-16">
          <RefreshCw className="w-6 h-6 text-primary-500 animate-spin mr-3" />
          <span className="text-gray-600">Loading FSP keys…</span>
        </div>
      )}

      {error && (
        <div className="p-4 bg-danger-50 border border-danger-200 rounded-xl text-sm text-danger-700 flex items-center gap-2">
          <XCircle className="w-4 h-4 flex-shrink-0" />
          {error}
          <button onClick={load} className="ml-auto text-danger-600 underline text-xs">Retry</button>
        </div>
      )}

      {/* Hub Key section */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <Server className="w-4 h-4 text-blue-600" />
          <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Hub / Switch Key</h2>
          <span className="text-xs text-gray-400">(used to verify callbacks from the Mojaloop Hub)</span>
        </div>

        {hubKeys.length === 0 && !loading ? (
          <div className="border-2 border-dashed border-blue-200 rounded-xl p-8 text-center">
            <Server className="w-8 h-8 text-blue-300 mx-auto mb-2" />
            <p className="text-sm font-medium text-gray-700">No Hub key registered</p>
            <p className="text-xs text-gray-500 mt-1 mb-4">
              Import the PEM-encoded public key provided by your Hub operator to enable JWS verification.
            </p>
            {canManage ? (
            <button
              onClick={() => setModal({ mode: 'import', presetHubKey: true })}
              className="btn-primary text-sm flex items-center gap-2 mx-auto"
            >
              <Plus className="w-4 h-4" /> Import Hub Key
            </button>
            ) : (
              <p className="text-xs text-gray-500">An admin at your institution can import it.</p>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            {hubKeys.map(k => (
              <KeyCard
                key={k.id}
                keyData={k}
                onRotate={key => setModal({ mode: 'rotate', key })}
                onDeactivate={key => setConfirmDeactivate(key)}
                canManage={canManage}
                onRefresh={load}
              />
            ))}
          </div>
        )}
      </div>

      {/* Participant keys section */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <Key className="w-4 h-4 text-gray-500" />
          <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Participant FSP Keys</h2>
          <span className="text-xs text-gray-400">(other DFSPs whose callbacks you verify)</span>
        </div>

        {participantKeys.length === 0 && !loading ? (
          <div className="border border-gray-200 rounded-xl p-6 text-center text-gray-400 text-sm">
            No participant FSP keys registered.
          </div>
        ) : (
          <div className="space-y-3">
            {participantKeys.map(k => (
              <KeyCard
                key={k.id}
                keyData={k}
                onRotate={key => setModal({ mode: 'rotate', key })}
                onDeactivate={key => setConfirmDeactivate(key)}
                canManage={canManage}
                onRefresh={load}
              />
            ))}
          </div>
        )}
      </div>

      {/* Inactive keys */}
      {inactiveKeys.length > 0 && (
        <details className="group">
          <summary className="cursor-pointer text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1.5 select-none list-none">
            <ChevronRight className="w-4 h-4 group-open:rotate-90 transition-transform" />
            {inactiveKeys.length} inactive key{inactiveKeys.length > 1 ? 's' : ''} (rotation history)
          </summary>
          <div className="mt-3 space-y-3">
            {inactiveKeys.map(k => (
              <KeyCard
                key={k.id}
                keyData={k}
                onRotate={() => {}}
                onDeactivate={() => {}}
                onRefresh={load}
              />
            ))}
          </div>
        </details>
      )}

      {/* Import / Rotate modal */}
      {modal && (
        <KeyImportModal
          mode={modal.mode}
          existingKey={modal.key}
          presetHubKey={Boolean(modal.presetHubKey)}
          onClose={() => setModal(null)}
          onSuccess={modal.mode === 'rotate' ? handleRotateSuccess : handleImportSuccess}
        />
      )}

      {/* Deactivate confirm dialog */}
      {confirmDeactivate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl p-6 max-w-sm w-full mx-4">
            <h3 className="font-semibold text-gray-900 mb-2">Deactivate Key?</h3>
            <p className="text-sm text-gray-600 mb-1">
              FSP: <strong>{confirmDeactivate.fspId}</strong>
            </p>
            <p className="text-sm text-gray-600 mb-4">
              JWS verification for this FSP will fail until a replacement key is imported.
              The record is kept for audit history.
            </p>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setConfirmDeactivate(null)} className="btn-secondary">Cancel</button>
              <button
                onClick={() => handleDeactivate(confirmDeactivate)}
                className="btn-primary bg-danger-600 hover:bg-danger-700 border-danger-600"
              >
                Deactivate
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FspKeys;
